/**
 * @typedef {ReturnType<typeof import('widgets/forms/AjaxForm').default>} AjaxForm
 * @typedef {InstanceType<ReturnType<typeof import('widgets/forms/BasicInput').default>>} basicInput
 *
 */

/**
 * @param {AjaxForm} AjaxForm Base widget for extending
 * @returns {typeof AddressForm} Address Form widget
 */
export default function (AjaxForm) {
    /**
     * @category widgets
     * @subcategory forms
     * @class AddressForm
     * @augments AjaxForm
     * @classdesc Address form component with next features:
     * 1. Initiates form definition update and autocomplete config on country change event.
     * 2. After triggering state change on address autocomplete field, filles address fields with autocomplete data
     *
     * Widget has next relationship:
     * * After country changing updates autocomplete config by calling {@link AutocompleteField#updateState}
     *
     * >* As an address autocomplete provider could be used different 3d parties.
     * >* Class is not intended to be used directly, but instead concrete forms (like profile address, shipping / billing address) should extend this class.
     * @property {string[]} data-address-fields - Form address fields array
     * @property {boolean} [data-address-autocomplete-enabled=true] - Is address autocomplete enabled for the form
     * @property {string} [data-address-autocomplete-provider=googleAutocomplete] - An identifier of address autocomplete form field
     * @example <caption>Example of shipping form</caption>
     * <form
     *     autocomplete="on"
     *     novalidate
     *     data-widget="shippingForm"
     *     data-event-submit.prevent="handleSubmit"
     *     ...
     *     data-address-fields="${[
     *         shippingFields.firstName.htmlName,
     *         shippingFields.lastName.htmlName,
     *         shippingFields.address1.htmlName,
     *         shippingFields.address2.htmlName,
     *         shippingFields.country.htmlName,
     *         shippingFields.states.stateCode.htmlName,
     *         shippingFields.city.htmlName,
     *         shippingFields.postalCode.htmlName,
     *         shippingFields.phone.htmlName
     *     ].join(',')}"
     *     ...
     *     data-form-definition-url="${URLUtils.url('Forms-FormDefinition', 'form', 'shipping')}"
     *     <isprint value=${pdict.forms.shippingForm.attributes} encoding="off"/>
     *     data-address-autocomplete-enabled="${!!require('util/sitepref').getValue('googleAutocompleteEnabled')}"
     * >
     *     ...
     *     <div
     *         class="b-form-message"
     *         role="alert"
     *         hidden="hidden"
     *         data-ref="errorMessageLabel"
     *     ></div>
     *     ...
     * </form>
     */
    class AddressForm extends AjaxForm {
        prefs() {
            return {
                addressFields: '',
                addressAutocompleteEnabled: true,
                addressAutocompleteProvider: 'googleAutocomplete',
                ...super.prefs()
            };
        }

        /**
         * @description Update form html body, after country change
         * @listens InputSelect#change
         * @param {basicInput} el - Country code field
         * @returns {void}
         */
        onCountryChange(el) {
            this.config.countryCode = el.getValue();

            this.updateFormData({ countryCode: this.config.countryCode })
                .then(() => this.updateAddressAutocomplete()).then(() => {
                    var currentSelector = document.querySelector(`form[data-address-selector="billingAddressSelector"]`);
                    if (currentSelector) {
                        var stateCode = currentSelector.querySelector(`input[id$="states_stateCode"]`);
                        var postalCode = currentSelector.querySelector(`input[id$="addressFields_postalCode"]`);
                        if (stateCode && postalCode) {
                            stateCode.value = '';
                            postalCode.value = '';
                        }
                    }
                });
        }

        /**
         * @description Get address fields from prefs
         * @returns {string[]} Address fields names, separated by a comma
         */
        getAddressFields() {
            return this.prefs().addressFields.split(',');
        }

        /**
         * @description Update address fields with new address object
         * @listens GoogleAutoComplateField#placechanged
         * @param {basicInput} el - Autocomplete input field
         * @param {object} address - Address object
         * @param {string} [address.address1] - Address 1
         * @param {string} [address.city] - City
         * @param {string} [address.county] - County
         * @param {string} [address.postalCode] - Postal Code
         * @param {string} [address.stateCode] - State code
         * @returns {void}
         */
        handlePlaceChange(el, address) {
            this.getAddressFields().forEach(item => {
                this.getById(item, (/** @type {basicInput} */addressField) => {
                    const name = addressField.getName() || '';
                    const shortName = name.split('_').pop();

                    if (shortName && (shortName in address)) {
                        addressField.setValue(address[shortName] || '', true);
                    }
                });
            });
        }

        /**
         * @description Handles address autocomplete update with the current country code
         * @returns {void}
         */
        updateAddressAutocomplete() {
            if (this.prefs().addressAutocompleteEnabled) {
                this.getById(this.prefs().addressAutocompleteProvider, (autocompleteField) => {
                    autocompleteField.updateState(this.config.countryCode);
                });
            }
        }
    }

    return AddressForm;
}
